import { useCallback, useEffect, useMemo, useState } from 'react'
import { ModalEditComponentProps } from '../../../../../../../baseComponents/ModalsLayouts/types'
import { TwinCalendarStartEndStateFull } from '../../../../../../../baseComponents/TwinCalendar/TwinCalendarStartEnd'
import { FormRender } from '../../../../../../../forms/FormRenderer/types'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { getAllWorkingCenters, getGroups } from '../../../../../../../utils/reducers/getters'
import { EmployeeModelType } from '@teinor/erp/types/company/userInner/employee'
import { twinFetchPostJSON } from '../../../../../../../utils/globals/data'
import { ModalCreateEditMedium } from '../../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { dictionaryComplexFromJsonArr, TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import useEditDeleteModal from '../../../../../../../utils/hooks/useEditDeleteModal'
import { BeforeSubmitHandler } from '../../../../../../../forms/TwinForm/types'
import ModalSureDeleteTurn from './ModalSureDeleteTurn'

type ModalDeleteWCalTurnProps = ModalEditComponentProps & {}

const ModalDeleteWCalTurn: React.FC<ModalDeleteWCalTurnProps> = ({ setOpened, onSubmit }) => {
    const { t } = useTwinTranslation()
    const workingCenters = useMemo(() => JSON.parse(JSON.stringify(getAllWorkingCenters())), [])
    const groups = useMemo(() => JSON.parse(JSON.stringify(getGroups())), [])
    const { handleOnChangeFields, allEmployees, handleBeforeSubmit, openModal } = useModalDeleteWCalTurnLogic()
    let fields: FormRender<TwinDictionary> = [
        {
            cols: 1,
            elements: [
                {
                    name: 'dates',
                    component: 'Custom',
                    render: () => {
                        return (
                            <div>
                                <div className='regular18'>{t('deleteDates', 'Fechas a eliminar')}</div>
                                <div className='border-solid border-b border-gray-D6 mt-20'>
                                    <TwinCalendarStartEndStateFull nameStart='dateStart' nameEnd='dateEnd' />
                                </div>
                            </div>
                        )
                    }
                },
            ],
        },
        {
            cols: 1,
            title: t('workingCentersToDelete', 'Centros de trabajo a eliminar'),
            elements: [
                {
                    name: 'workingCenters',
                    component: 'MultipleCheckboxSelector',
                    subtitles: {
                        subtitleOptions: t('workingCenters', 'Centros de trabajo'),
                        subtitleSelectedOptions: t('workingCentersSelected', 'Centros de trabajo seleccionados')
                    },
                    items: workingCenters || {},
                    onChange: (value) => handleOnChangeFields(value, 'workingCenters')
                },
            ]
        },
        {
            cols: 1,
            title: t('groupstoDelete', 'Grupos a eliminar'),
            elements: [
                {
                    name: 'groups',
                    component: 'MultipleCheckboxSelector',
                    subtitles: {
                        subtitleOptions: t('groups', 'Grupos'),
                        subtitleSelectedOptions: t('groupsSelected', 'Grupos seleccionados')
                    },
                    items: groups || {},
                    onChange: (value) => handleOnChangeFields(value, 'groups')
                },
            ]
        },
        {
            cols: 1,
            title: t('employeesToDelete', 'Empleados a eliminar'),
            elements: [
                {
                    name: 'employees',
                    component: 'MultipleCheckboxSelector',
                    subtitles: {
                        subtitleOptions: t('employees', 'Empleados'),
                        subtitleSelectedOptions: t('employeesSelected', 'Empleados seleccionados')
                    },
                    items: dictionaryComplexFromJsonArr(allEmployees || {}),
                    keyName: 'fullname_short'
                },
            ]
        }
    ]
    
    return (
        <ModalCreateEditMedium setOpened={setOpened} onSubmit={() => {}} fields={fields} translations={{
            title: t('deleteTurns', 'Eliminar turnos'),
            button: t('delete', 'Eliminar'),
        }} haveButtonPermissions={true} url='' className='deleteModalWCalTurns' beforeSubmit={handleBeforeSubmit}>
            {openModal?.type === 'delete' && <ModalSureDeleteTurn {...{ setOpened: () => setOpened(null) }} allRowData={openModal.allRowData} onSubmit={(res, send) => { onSubmit(res, send); setOpened(null) }} />}
        </ModalCreateEditMedium>
    )
}

interface TurnsToDuplicateFields {
    workingCenters: number[]
    groups: number[]
    employees: number[]
}

const useModalDeleteWCalTurnLogic = () => {
    const [selectedFields, setSelectedFields] = useState<TurnsToDuplicateFields>({
        workingCenters: [],
        groups: [],
        employees: [],
    })
    const [allEmployees, setAllEmployees] = useState<EmployeeModelType[] | null>(null)
    const { openModal, setOpenModal } = useEditDeleteModal()

    const getEmployees = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/employee/getAllEmployeesByWorkingCenterAndGroup', {
            WorkingCenterIds: selectedFields.workingCenters,
            GroupIds: selectedFields.groups
        })
        if (result) {
            setAllEmployees(result)
        }
    }, [selectedFields, setAllEmployees])

    const handleOnChangeFields = useCallback((value: any, field: keyof TurnsToDuplicateFields) => {
        setSelectedFields((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            return { ...copy, [field]: value }
        })
    }, [setSelectedFields])

    const handleBeforeSubmit: BeforeSubmitHandler = useCallback((vals) => {
        setOpenModal({ type: 'delete', allRowData: vals })
        return false
    }, [setOpenModal])

    useEffect(() => {
        getEmployees()
    }, [getEmployees])

    return { handleOnChangeFields, selectedFields, allEmployees, handleBeforeSubmit, openModal }
}

export default ModalDeleteWCalTurn